import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import PrimaryButton from "../../../../components/Button/PrimaryButton";
import AuthLayout from "../../../../components/Auth/AuthLayout";
import AuthBanner from "../../../../components/Auth/AuthBanner";
import AuthFormLayout from "../../../../components/Auth/AuthFormLayout";
import AuthField from "../../../../components/Auth/AuthField";
import PasswordStrengthMeter from "../../../../components/Auth/PasswordStrengthMeter";
import api from "../../../../utils/axiosInstance";
import { AUTH_MESSAGES } from "../../../../utils/constants/authMessages";
import { passwordPolicyMessage } from "../../../../utils/passwordPolicy";

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();

  const [form, setForm] = useState({ newPassword: "", confirmPassword: "" });
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const [success, setSuccess] = useState("");

  const validate = () => {
    const currentErrors = {};
    const passwordError = passwordPolicyMessage(form.newPassword);
    if (passwordError) currentErrors.newPassword = passwordError;
    if (!form.confirmPassword) {
      currentErrors.confirmPassword = "Confirm password is required.";
    } else if (form.newPassword !== form.confirmPassword) {
      currentErrors.confirmPassword = "Passwords do not match.";
    }
    setErrors(currentErrors);
    return Object.keys(currentErrors).length === 0;
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    let errorMsg = "";
    if (name === "newPassword") {
      errorMsg = passwordPolicyMessage(value);
    } else if (!value) {
      errorMsg = "Confirm password is required.";
    }
    if (name === "confirmPassword" && value !== form.newPassword) {
      errorMsg = "Passwords do not match.";
    }
    setErrors((prev) => ({ ...prev, [name]: errorMsg }));
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: "" });
    setServerError("");
    setSuccess("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      const res = await api.post("/auth/reset-password", {
        token,
        newPassword: form.newPassword,
      });

      setSuccess(res.data.message || AUTH_MESSAGES.resetSuccess);
      setTimeout(() => navigate("/login"), 2000); // redirect after success
    } catch (err) {
      setServerError(err.response?.data?.error || AUTH_MESSAGES.resetFailed);
    }
  };

  if (!token) {
    return (
      <AuthLayout>
        <h1 className="text-center text-[28px] font-medium leading-tight text-gray-900">
          Reset Password
        </h1>
        <AuthBanner error="This reset link is invalid or has expired." />
        <div className="mt-8">
          <PrimaryButton type="button" onClick={() => navigate("/request-reset")}>
            Request a New Link
          </PrimaryButton>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthFormLayout
      title="Reset Password"
      error={serverError}
      success={success}
      onSubmit={handleSubmit}
      footer={
        <p className="text-center text-xs font-medium text-gray-500">
          Remembered it?{" "}
          <Link to="/login" className="font-bold text-brand-600 hover:underline">
            Log In
          </Link>
        </p>
      }
    >
      <AuthField
        name="newPassword"
        label="New Password"
        type="password"
        placeholder="Enter new password"
        autoComplete="new-password"
        value={form.newPassword}
        error={errors.newPassword}
        onChange={handleChange}
        onBlur={handleBlur}
      />

      <PasswordStrengthMeter password={form.newPassword} />

      <AuthField
        name="confirmPassword"
        label="Confirm Password"
        type="password"
        placeholder="Re-enter password"
        autoComplete="new-password"
        value={form.confirmPassword}
        error={errors.confirmPassword}
        onChange={handleChange}
        onBlur={handleBlur}
      />

      <PrimaryButton type="submit">Reset Password</PrimaryButton>
    </AuthFormLayout>
  );
};

export default ResetPassword;
